import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Phone, Mail, CheckCircle, Users
} from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import SEO from '@/components/shared/SEO';

// Service includes data
const serviceIncludes = [
  {
    iconSrc: '/Assets/icons/supply-material_icon.svg',
    title: 'Material Supply',
    description: 'High-quality commercial flooring materials sourced for performance and durability.'
  },
  {
    iconSrc: '/Assets/icons/turnkey-install_icon.svg',
    title: 'Turnkey Installation Nationwide',
    description: 'Professional installation services coordinated and managed anywhere in the United States.'
  },
  {
    iconSrc: '/Assets/icons/product-assort_icon.svg',
    title: 'Product Assortment',
    products: [
      'Heavy-Duty Carpet Tile',
      'Entrance Grid Systems',
      'Luxury Vinyl Plank & Tile (LVP/LVT)',
      'Non-Slip Flooring Solutions'
    ]
  },
  {
    iconSrc: '/Assets/icons/maintenance_icon.svg',
    title: 'Flooring Maintenance & Restoration',
    products: [
      'Scheduled maintenance programs',
      'Deep cleaning and restoration services',
      'Solutions designed to extend the life of your flooring investment'
    ]
  }
];

const ConciergePage = () => {
  return (
    <Layout>
      <SEO 
        title="Flooring Concierge Service"
        description="Full-service flooring solutions from Portico. Our concierge service handles everything from material selection to installation, project management, and ongoing maintenance."
        canonicalUrl="/concierge"
      />
      {/* Hero Section */}
      <section className="bg-primary-bg text-primary-foreground py-16 md:py-20">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6" data-testid="concierge-hero-title">
              Concierge Flooring Services
            </h1>
            <p className="text-xl text-primary-foreground/90">
              At Portico Systems, our Concierge Service is designed to make commercial flooring simple, efficient, and stress-free. Whether you're planning a new installation, replacing existing flooring, or maintaining your current system, our team provides the expertise and support to deliver the right solution for your space.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Flooring Partner Section */}
      <section className="section-padding bg-muted">
        <div className="container-portico">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Users className="w-12 h-12 text-primary mb-6" />
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                A Flooring Partner Focused on Your Business
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Your space is one of the most visible reflections of your brand. Clean, safe, and well-maintained flooring helps create a positive environment for employees, customers, and visitors while protecting your investment.
                </p>
                <p>
                  Our concierge approach ensures that your flooring systems are selected, installed, and maintained to maximize performance, durability, and long-term value, allowing you to focus on what matters most: running your business.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="/Assets/images/services/flooring-partner.jpg"
                alt="Concierge Flooring Services"
                className="rounded-2xl shadow-xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Our Service Includes */}
      <section className="section-padding bg-background" data-testid="concierge-services-section">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              What Our Concierge Service Includes
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our team provides end-to-end support across all aspects of commercial flooring.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {serviceIncludes.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-portico p-6 md:p-8"
                data-testid={`service-card-${index}`}
              >
                <img 
                  src={service.iconSrc} 
                  alt={service.title}
                  className="w-10 h-10 mb-4"
                />
                <h3 className="font-heading font-semibold text-xl mb-4">{service.title}</h3>
                
                {service.description && (
                  <p className="text-muted-foreground">{service.description}</p>
                )}
                
                {service.products && (
                  <ul className="space-y-2">
                    {service.products.map((product) => (
                      <li key={product} className="flex items-start gap-2 text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-secondary flex-shrink-0 mt-1" />
                        <span>{product}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Single Point of Contact Section */}
      <section className="section-padding bg-secondary text-secondary-foreground">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Your Single Point of Contact
            </h2>
            <p className="text-secondary-foreground/90 text-lg">
              With Portico Systems' Concierge Service, you gain a trusted partner who manages the details so you don't have to. Our team provides quotes, coordinates installation schedules, and ensures you always have the information you need to make the right decision for your facility.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Get Started CTA */}
      <section className="section-padding bg-background">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-primary-bg text-primary-foreground rounded-2xl p-8 md:p-12"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                  Get Started
                </h2>
                <p className="text-primary-foreground/90 mb-6">
                  Speak with your representative or contact us today to learn how our Concierge Service can support your next flooring project.
                </p>
                
                {/* Contact Info */}
                <div className="space-y-4 mb-8">
                  <a
                    href="tel:+18772858899"
                    className="flex items-center gap-3 text-primary-foreground/90 hover:text-primary-foreground transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    <span>+1 877 285 8899</span>
                  </a>
                  <a
                    href="mailto:sales@porticosystems.com"
                    className="flex items-center gap-3 text-primary-foreground/90 hover:text-primary-foreground transition-colors"
                  >
                    <Mail className="w-5 h-5" />
                    <span>sales@porticosystems.com</span>
                  </a>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
                <Link to="/contact">
                  <Button variant="secondary" size="lg" data-testid="concierge-contact-btn">
                    Contact Us
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/products">
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                  >
                    View Products
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default ConciergePage;
